import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { rupees } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, setQuantity, removeItem, subtotalPaise } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-[#E8F3EB] border-l border-[#D1E2D6] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#D1E2D6]">
          <SheetTitle className="font-display text-3xl text-[#1B4332]" data-testid="cart-drawer-title">
            Your ritual
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center">
            <div className="grid place-items-center h-16 w-16 rounded-full border border-[#D1E2D6] text-[#4A6B56]">
              <ShoppingBag size={22} strokeWidth={1.5} />
            </div>
            <p className="text-[#4A6B56]">Your cart is feeling empty.</p>
            <Link
              to="/shop"
              data-testid="cart-empty-shop-link"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center rounded-full bg-[#1B4332] text-[#E8F3EB] px-8 h-12 font-medium hover:bg-[#2D5A44] transition-colors duration-300"
            >
              Start your ritual
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-testid="cart-drawer-items">
              {items.map((item) => (
                <div
                  key={item.sku}
                  data-testid={`cart-item-${item.sku}`}
                  className="flex gap-4 rounded-2xl bg-white/60 border border-[#D1E2D6] p-3"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-20 w-20 rounded-xl object-cover"
                  />
                  <div className="flex-1 flex flex-col justify-between py-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display text-xl leading-tight text-[#1B4332]">{item.name}</p>
                        <p className="text-xs text-[#4A6B56] mt-0.5">{item.sachets} sachets</p>
                      </div>
                      <button
                        data-testid={`cart-item-remove-${item.sku}`}
                        onClick={() => removeItem(item.sku)}
                        className="text-[#4A6B56] hover:text-[#1B4332] transition-colors"
                        aria-label={`Remove ${item.name}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 rounded-full border border-[#D1E2D6] px-2 py-1">
                        <button
                          data-testid={`cart-item-decrement-${item.sku}`}
                          onClick={() => setQuantity(item.sku, item.quantity - 1)}
                          className="grid place-items-center h-6 w-6 rounded-full hover:bg-[#E8F3EB] transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={13} />
                        </button>
                        <span data-testid={`cart-item-qty-${item.sku}`} className="text-sm font-semibold w-4 text-center">
                          {item.quantity}
                        </span>
                        <button
                          data-testid={`cart-item-increment-${item.sku}`}
                          onClick={() => setQuantity(item.sku, item.quantity + 1)}
                          className="grid place-items-center h-6 w-6 rounded-full hover:bg-[#E8F3EB] transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <p className="font-semibold text-[#1B4332]" data-testid={`cart-item-price-${item.sku}`}>
                        {rupees(item.price_paise * item.quantity)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-6 border-t border-[#D1E2D6] bg-white/50 space-y-4">
              <div className="flex justify-between text-[#4A6B56] text-sm">
                <span>Shipping</span>
                <span>Free, across India</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-[#4A6B56]">Subtotal</span>
                <span data-testid="cart-drawer-subtotal" className="font-display text-3xl text-[#1B4332]">
                  {rupees(subtotalPaise)}
                </span>
              </div>
              <Link
                to="/checkout"
                data-testid="cart-drawer-checkout-button"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center w-full rounded-full bg-[#1B4332] text-[#E8F3EB] h-14 font-medium hover:bg-[#2D5A44] transition-colors duration-300"
              >
                Checkout
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
